import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Plus,
  Trash2,
  Save,
  Calculator,
  User,
  Building2,
  HelpCircle
} from 'lucide-react';
import Layout from '../components/Layout';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import { invoiceAPI, clientAPI, companyAPI } from '../services/api';
import { supplierAPI } from '../services/supplierApi';
import { productAPI } from '../services/productApi';
import { InvoiceRequest, OrderItem, Client, Supplier, Company, Product, PaymentMethod, Currency, InvoiceType } from '../types';
import OrderItemModal from '../components/OrderItemModal';
import CashThresholdWarningModal from '../components/CashThresholdWarningModal';
import { getErrorMessage } from '../utils/errorHandler';
import { calculateTotalInWords, Language } from '../utils/numberToWords';
import { setEncryptionEnabled } from '../utils/EncryptionUtils';

const InvoiceCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedCompany } = useCompany();
  const { t, isRTL, currentLanguage } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showOrderItemModal, setShowOrderItemModal] = useState(false);
  const [editingOrderItemIndex, setEditingOrderItemIndex] = useState<number | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);

  // Cash threshold modal state
  const [showCashThresholdModal, setShowCashThresholdModal] = useState(false);
  const [cashTotals, setCashTotals] = useState<{ dailyTotal: number; monthlyTotal: number }>({ dailyTotal: 0, monthlyTotal: 0 });
  const [pendingInvoiceData, setPendingInvoiceData] = useState<InvoiceRequest | null>(null);

  const [formData, setFormData] = useState<InvoiceRequest>({
    issuerId: selectedCompany.selectedCompanyId || '',
    clientId: '',
    orderId: '',
    invoiceNumber: undefined,
    creationDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    currency: 'MAD',
    invoiceType: searchParams.get('type') || '', // Get from URL parameter if available
    paymentMethod: 'CASH',
    totalInWords: '',
    language: selectedCompany.selectedCompany?.lang || 'fr',
    order: {
      items: [],
      vatRate: 20,
      discount: 0,
      discountAmount: 0
    }
  });

  //Load initial data
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && selectedCompany.selectedCompany) {
      // Ensure encryption is enabled if the company has it
      setEncryptionEnabled(!!selectedCompany.selectedCompany.encryptionFeatureEnabled);
      loadClients();
      loadSuppliers();
      loadProducts();
      loadCompany();

      // Check for initial data from navigation state
      const initialData = location.state?.initialData;

      setFormData(prev => ({
        ...prev,
        issuerId: selectedCompany.selectedCompanyId || '',
        // Override with initial data if present
        ...(initialData || {})
      }));

      //TODO idalic check this should be enhanced avoid using a separate TODO;
      setDiscountAmount(initialData?.order?.discountAmount || 0);

      // Set orderId if present in initialData
      if (initialData?.orderId) {
        setFormData(prev => ({ ...prev, orderId: initialData.orderId }));
      }
    }
  }, [selectedCompany.selectedCompanyId, selectedCompany.selectedCompany, location.state]);

  // Load invoice number when invoice type is available
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && formData.invoiceType) {
      loadNextInvoiceNumber();
    }
  }, [selectedCompany.selectedCompanyId, formData.invoiceType, formData.creationDate]);

  // Auto-update totalInWords when orderItems, discount, or VAT rate changes
  // Auto-update totalInWords when items, discount, or VAT rate changes
  useEffect(() => {
    if (formData.order?.items && formData.order.items.length > 0) {
      const calculatedTotalInWords = calculateTotalInWordsLocal();
      setFormData(prev => ({ ...prev, totalInWords: calculatedTotalInWords }));
    }
  }, [formData.order?.items, formData.order?.discount, formData.order?.discountAmount, formData.order?.vatRate, formData.currency, currentLanguage]);

  // Auto-start onboarding tour on first visit
  const { startTour, manualStart } = useOnboardingTour();
  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      startTour({
        tourId: 'invoice-create',
        steps: getInvoiceTourSteps(),
        autoStartDelay: 1200,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.selectedCompanyId]);

  const loadClients = async () => {
    if (!selectedCompany.selectedCompanyId) return;

    try {
      const clientsData = await clientAPI.getClientsByCompany(selectedCompany.selectedCompanyId);
      setClients(clientsData);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadSuppliers = async () => {
    if (!selectedCompany.selectedCompanyId) return;

    try {
      const suppliersData = await supplierAPI.getSuppliersByCompany(selectedCompany.selectedCompanyId);
      setSuppliers(suppliersData);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  };

  const loadProducts = async () => {
    if (!selectedCompany.selectedCompanyId) return;

    try {
      const productsData = await productAPI.getProductsByCompany(selectedCompany.selectedCompanyId);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadCompany = async () => {
    if (!selectedCompany.selectedCompanyId) return;

    try {
      const companyData = await companyAPI.getCompanyById(selectedCompany.selectedCompanyId);
      setCompany(companyData);

      // Update formData with company's VAT rate
      setFormData(prev => ({
        ...prev,
        order: {
          ...prev.order,
          vatRate: companyData.vatRate || 20 // Default to 20 if not set
        }
      }));
    } catch (err) {
      console.error('Error loading company:', err);
    }
  };

  const loadNextInvoiceNumber = async () => {
    if (!selectedCompany.selectedCompanyId || !formData.creationDate) return;

    try {
      const selectedYear = new Date(formData.creationDate).getFullYear();
      const nextNumber = await invoiceAPI.getNextInvoiceNumberForYearAndType(selectedCompany.selectedCompanyId, selectedYear, formData.invoiceType);
      setFormData(prev => ({ ...prev, invoiceNumber: nextNumber }));
    } catch (err) {
      console.error('Error loading next invoice number:', err);
    }
  };

  const handleInputChange = (field: keyof InvoiceRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // If creation date changes, update the invoice number for the new year
    if (field === 'creationDate' && typeof value === 'string') {
      const selectedYear = new Date(value).getFullYear();
      if (selectedCompany.selectedCompanyId) {
        invoiceAPI.getNextInvoiceNumberForYearAndType(selectedCompany.selectedCompanyId, selectedYear, formData.invoiceType)
          .then(nextNumber => {
            setFormData(prev => ({ ...prev, invoiceNumber: nextNumber }));
          })
          .catch(err => {
            console.error('Error updating invoice number for new date:', err);
          });
      }
    }

    // If invoice type changes, update the invoice number for the new type
    if (field === 'invoiceType' && typeof value === 'string') {
      const selectedYear = new Date(formData.creationDate || new Date().toISOString().split('T')[0]).getFullYear();
      if (selectedCompany.selectedCompanyId) {
        invoiceAPI.getNextInvoiceNumberForYearAndType(selectedCompany.selectedCompanyId, selectedYear, value)
          .then(nextNumber => {
            setFormData(prev => ({ ...prev, invoiceNumber: nextNumber }));
          })
          .catch(err => {
            console.error('Error updating invoice number for new type:', err);
          });
      }
    }
  };

  const handleOrderChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      order: {
        ...prev.order,
        [field]: value
      }
    }));
  };


  const handleOrderItemSave = (item: OrderItem) => {
    if (editingOrderItemIndex !== null) {
      // Edit existing product
      const updatedOrderItems = [...(formData.order.items || [])];
      updatedOrderItems[editingOrderItemIndex] = item;
      setFormData(prev => ({
        ...prev,
        order: {
          ...prev.order,
          items: updatedOrderItems
        }
      }));
      setEditingOrderItemIndex(null);
    } else {
      // Add new product
      setFormData(prev => ({
        ...prev,
        order: {
          ...prev.order,
          items: [...(prev.order.items || []), item]
        }
      }));
    }
    setShowOrderItemModal(false);
  };

  const handleEditOrderItem = (index: number) => {
    setEditingOrderItemIndex(index);
    setShowOrderItemModal(true);
  };

  const handleDeleteOrderItem = (index: number) => {
    const updatedOrderItems = formData.order.items?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({
      ...prev,
      order: {
        ...prev.order,
        items: updatedOrderItems
      }
    }));
  };

  const calculateSubtotal = () => {
    return formData.order.items?.reduce((sum, orderItem) => {
      const OrderItemTotal = orderItem.unitPrice * orderItem.quantity;
      // Round to 2 decimal places to avoid floating point issues
      return Math.round((sum + OrderItemTotal) * 100) / 100;
    }, 0) || 0;
  };

  const calculateDiscountAmount = () => {
    // Use discountAmount directly as the source of truth
    return Math.round((discountAmount || 0) * 100) / 100;
  };

  const calculateTotalAfterDiscount = () => {
    const total = calculateSubtotal() - calculateDiscountAmount();
    return Math.round(total * 100) / 100;
  };

  const calculateVAT = () => {
    const vatAmount = (calculateTotalAfterDiscount() * (formData.order.vatRate || 0)) / 100;
    return Math.round(vatAmount * 100) / 100;
  };

  const calculateTotal = () => {
    const total = calculateTotalAfterDiscount() + calculateVAT();
    return Math.round(total * 100) / 100;
  };

  // Function to convert number to words (simplified version)
  const calculateTotalInWordsLocal = () => {
    const total = calculateTotal();
    const language = currentLanguage as Language;
    return calculateTotalInWords(total, formData.currency, language);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency || 'MAD',
    }).format(amount);
  };

  const formatInvoiceNumberWithDate = () => {
    if (!formData.invoiceNumber || !formData.creationDate) return '';

    const date = new Date(formData.creationDate);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${formData.invoiceNumber.toString().padStart(6, '0')}/${day}${month}${year}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clientId) {
      const entityLabel = formData.invoiceType === InvoiceType.PURCHASE_INVOICE
        ? t('invoices.supplier')
        : t('invoices.client');
      setError(`Please select a ${entityLabel}`);
      return;
    }

    if (!formData.order.items || formData.order.items.length === 0) {
      setError('Please add at least one Order Item');
      return;
    }

    // Check cash threshold for SELL_INVOICE with CASH payment
    const isSellInvoice = formData.invoiceType === InvoiceType.SELL_INVOICE || formData.invoiceType === 'SELL_INVOICE';
    const isCashPayment = formData.paymentMethod === PaymentMethod.CASH || formData.paymentMethod === 'CASH';
    const hasCashThreshold = company?.cashThresholdEnabled && (company?.dailyCashThreshold || company?.monthlyCashThreshold);

    if (isSellInvoice && isCashPayment && hasCashThreshold) {
      try {
        const totals = await invoiceAPI.getCashTotals(selectedCompany.selectedCompanyId!);
        setCashTotals(totals);

        const invoiceAmount = calculateTotal();
        const newDailyTotal = totals.dailyTotal + invoiceAmount;
        const newMonthlyTotal = totals.monthlyTotal + invoiceAmount;

        const dailyExceeded = company.dailyCashThreshold && newDailyTotal > company.dailyCashThreshold;
        const monthlyExceeded = company.monthlyCashThreshold && newMonthlyTotal > company.monthlyCashThreshold;

        if (dailyExceeded || monthlyExceeded) {
          // Store the pending invoice data and show the warning modal
          const invoiceData = {
            ...formData,
            order: {
              ...formData.order,
              discountAmount: discountAmount
            },
            totalInWords: formData.totalInWords || calculateTotalInWordsLocal()
          };
          setPendingInvoiceData(invoiceData);
          setShowCashThresholdModal(true);
          return;
        }
      } catch (err) {
        console.error('Error checking cash thresholds:', err);
        // Continue with invoice creation if threshold check fails
      }
    }

    await submitInvoice();
  };

  const submitInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      // If this was a conversion from a quote, deactivate the original quote
      // Use type assertion for location.state as it's unknown
      const state = location.state as { originalQuoteId?: string } | null;
      const originalQuoteId = state?.originalQuoteId;

      // Use pending data if available (from modal confirmation), otherwise prepare fresh
      const invoiceData = pendingInvoiceData || {
        ...formData,
        order: {
          ...formData.order,
          discountAmount: discountAmount
        },
        totalInWords: formData.totalInWords || calculateTotalInWordsLocal(),
        originalQuoteId: originalQuoteId,
      };

      const newInvoice = await invoiceAPI.createInvoice(invoiceData);

      if (originalQuoteId && newInvoice.invoiceType === "QUOTE") {
        await invoiceAPI.linkQuoteToInvoice(newInvoice.id, originalQuoteId);
      }

      // Reset pending data
      setPendingInvoiceData(null);

      navigate('/invoices', {
        state: {
          message: `Invoice "${newInvoice.formattedInvoiceNumber}" created successfully!`,
          type: 'success'
        }
      });
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCashThresholdConfirm = async () => {
    setShowCashThresholdModal(false);
    await submitInvoice();
  };

  const handleCashThresholdCancel = () => {
    setShowCashThresholdModal(false);
    setPendingInvoiceData(null);
  };

  // Show message if no company is selected
  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
            <p className="text-gray-500 mb-4">
              Please select a company from the dropdown to create an invoice.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/invoices')}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('invoices.backToInvoices')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <DollarSign className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('invoices.createNewInvoice')}</h1>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div id="invoice-basic-info" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className={`text-xl font-semibold text-gray-900 mb-6 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
              <Calculator className="w-5 h-5" />
              <span>{t('invoices.invoiceInformation')}</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.invoiceNumber')} *
                </label>
                <input
                  type="number"
                  required
                  value={formData.invoiceNumber?.toString() || ''}
                  onChange={(e) => handleInputChange('invoiceNumber', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('invoices.invoiceNumberPlaceholder')}
                />
              </div>

              {/* Creation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.creationDate')} *
                </label>
                <input
                  type="date"
                  required
                  value={formData.creationDate || ''}
                  onChange={(e) => handleInputChange('creationDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {formData.invoiceNumber && formData.creationDate && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t('invoices.formattedReference') || 'Formatted'}: {formatInvoiceNumberWithDate()}
                  </p>
                )}
              </div>

              {/* Deadline Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.deadlineDate')}
                </label>
                <input
                  type="date"
                  value={formData.deadlineDate || ''}
                  onChange={(e) => handleInputChange('deadlineDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Client/Supplier Selection - Dynamic based on invoice type */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                    ? t('invoices.supplier')
                    : t('invoices.client')} *
                </label>
                <select
                  required
                  value={formData.clientId}
                  onChange={(e) => handleInputChange('clientId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value=''>
                    {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                      ? t('invoices.selectSupplier')
                      : t('invoices.selectClient')}
                  </option>
                  {formData.invoiceType === InvoiceType.PURCHASE_INVOICE
                    ? suppliers.map((supplier) =>
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    )
                    : clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.clientNumber && `(${client.clientNumber})`}
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Invoice Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.invoiceType')} *
                </label>
                <select
                  required
                  value={formData.invoiceType}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                >
                  <option value={formData.invoiceType}>
                    {t(`invoiceEdit.types.${formData.invoiceType}`)}
                  </option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('invoices.invoiceTypeCannotBeChanged')}
                </p>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.currency')} *
                </label>
                <select
                  required
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.values(Currency).map((currency) => (
                    <option key={currency} value={currency}>
                      {currency} – {t('companies.currency' + currency.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.paymentMethod')} *
                </label>
                <select
                  required
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.values(PaymentMethod).map((method) => (
                    <option key={method} value={method}>
                      {t(`common.payments.${method}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* VAT Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.vatRate')} (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  required
                  value={formData.order.vatRate}
                  onChange={(e) => handleOrderChange('vatRate', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="20"
                />
              </div>

              {/* Discount Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.discountPercentage')}
                </label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  max="100"
                  value={formData.order.discount || 0}
                  onChange={(e) => {
                    const percentage = parseFloat(e.target.value) || 0;
                    handleOrderChange('discount', percentage);
                    // Calculate value from percentage
                    const subtotal = calculateSubtotal();
                    const value = Math.round((subtotal * percentage / 100) * 100) / 100;
                    setDiscountAmount(value);
                    handleOrderChange('discountAmount', value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.discountAmount')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={discountAmount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setDiscountAmount(value);
                    handleOrderChange('discountAmount', value);
                    // Calculate percentage from value
                    const subtotal = calculateSubtotal();
                    const percentage = subtotal > 0 ? Math.round((value / subtotal * 100) * 100) / 100 : 0;
                    handleOrderChange('discount', Math.min(percentage, 100));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* OrderItems Section */}
          <div id="invoice-products" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold text-gray-900 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                <Plus className="w-5 h-5" />
                <span>{t('invoices.products')}</span>
              </h2>
              <button
                type="button"
                onClick={() => setShowOrderItemModal(true)}
                className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors`}
              >
                <Plus className="w-4 h-4" />
                <span>{t('invoices.addProduct')}</span>
              </button>
            </div>

            {formData.order.items && formData.order.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.code')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.description')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.price')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.quantity')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.unit')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.total')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoices.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.order.items.map((orderItem, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {orderItem.code}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {orderItem.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(orderItem.unitPrice)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {orderItem.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {orderItem.unity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(orderItem.unitPrice * orderItem.quantity)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className={`flex items-center justify-end ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                            <button
                              type="button"
                              onClick={() => handleEditOrderItem(index)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {t('invoices.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteOrderItem(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Plus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('invoices.noProductsAdded')}</h3>
                <p className="text-gray-500 mb-4">
                  {t('invoices.addProductsToGetStarted')}
                </p>
                <button
                  type="button"
                  onClick={() => setShowOrderItemModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('invoices.addFirstProduct')}
                </button>
              </div>
            )}
          </div>

          {/* Totals Section */}
          <div id="invoice-totals" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className={`text-xl font-semibold text-gray-900 mb-6 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
              <Calculator className="w-5 h-5" />
              <span>{t('invoices.invoiceTotals')}</span>
            </h2>

            <div className="max-w-md ml-auto space-y-4">
              <div className="flex justify-between text-sm">
                <span className='text-gray-600'>{t('invoices.subtotal')}:</span>
                <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
              </div>

              {formData.order.discount && formData.order.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className='text-gray-600'>{t('invoices.discount')} ({formData.order.discount}%):</span>
                  <span className="font-medium text-red-600">-{formatCurrency(calculateDiscountAmount())}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className='text-gray-600'>{t('invoices.totalAfterDiscount')}:</span>
                <span className="font-medium">{formatCurrency(calculateTotalAfterDiscount())}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className='text-gray-600'>{t('invoices.vat')} ({formData.order.vatRate}%):</span>
                <span className="font-medium">{formatCurrency(calculateVAT())}</span>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('invoices.total')}:</span>
                  <span>{formatCurrency(calculateTotal())}</span>
                </div>
              </div>

              {/* Total in Words */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoices.totalInWords')}
                </label>
                <input
                  type="text"
                  value={formData.totalInWords}
                  onChange={(e) => handleInputChange('totalInWords', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('invoices.totalInWordsPlaceholder')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('invoices.totalInWordsDescription')}
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div id="invoice-submit-actions" className={`flex items-center justify-end ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('invoices.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('invoices.creating')}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{t('invoices.createInvoice')}</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* OrderItem Modal */}
        <OrderItemModal
          isOpen={showOrderItemModal}
          onClose={() => {
            setShowOrderItemModal(false);
            setEditingOrderItemIndex(null);
          }}
          currency={formData.currency}
          onSave={handleOrderItemSave}
          item={editingOrderItemIndex !== null ? {
            id: formData.order.items?.[editingOrderItemIndex]?.id,
            companyId: selectedCompany.selectedCompanyId || '',
            code: formData.order.items?.[editingOrderItemIndex]?.code || '',
            description: formData.order.items?.[editingOrderItemIndex]?.description || '',
            unitPrice: formData.order.items?.[editingOrderItemIndex]?.unitPrice || 0,
            unity: formData.order.items?.[editingOrderItemIndex]?.unity || '',
            quantity: formData.order.items?.[editingOrderItemIndex]?.quantity || 1
          } : {
            companyId: selectedCompany.selectedCompanyId || '',
            code: '',
            description: '',
            unitPrice: 0,
            unity: '',
            quantity: 1
          }}
          mode={editingOrderItemIndex !== null ? 'edit' : 'create'}
          products={products}
        />

        {/* Cash Threshold Warning Modal */}
        <CashThresholdWarningModal
          isOpen={showCashThresholdModal}
          onClose={handleCashThresholdCancel}
          onConfirm={handleCashThresholdConfirm}
          dailyTotal={cashTotals.dailyTotal}
          monthlyTotal={cashTotals.monthlyTotal}
          dailyThreshold={company?.dailyCashThreshold || 0}
          monthlyThreshold={company?.monthlyCashThreshold || 0}
          invoiceAmount={calculateTotal()}
          currency={formData.currency}
        />

      </div>

      {/* Onboarding Tour Help Button */}
      <button
        type="button"
        className="onboarding-help-btn"
        title={t('onboarding.replayTour')}
        onClick={() => {
          manualStart({
            tourId: 'invoice-create',
            steps: getInvoiceTourSteps(),
          });
        }}
      >
        <HelpCircle size={22} />
      </button>
    </Layout>
  );

  function getInvoiceTourSteps() {
    return [
      {
        element: '#invoice-basic-info',
        popover: {
          title: t('onboarding.invoice.basicInfoTitle'),
          description: t('onboarding.invoice.basicInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#invoice-products',
        popover: {
          title: t('onboarding.invoice.productsTitle'),
          description: t('onboarding.invoice.productsDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#invoice-totals',
        popover: {
          title: t('onboarding.invoice.totalsTitle'),
          description: t('onboarding.invoice.totalsDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#invoice-submit-actions',
        popover: {
          title: t('onboarding.invoice.submitTitle'),
          description: t('onboarding.invoice.submitDesc'),
          side: 'top' as const,
          align: 'end' as const,
        },
      },
    ];
  }
};

export default InvoiceCreatePage;
